import React, { useEffect, useState } from 'react';
import { func, object, shape } from 'prop-types';
import { Redirect, withRouter } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';

import ActionBar from '../../components/ActionBar';
import ActionBarItem from '../../components/ActionBarItem';
import EsmsPage from '../../components/EsmsPage';
import usePermissions from '../../hooks/usePermissions';
import useRules from '../../hooks/useRules';
import useTableParams from '../../hooks/useTableParams';
import RuleDeleteConfirmModal from '../Modals/RuleDeleteConfirmModal';
import RuleDeleteSelectedModal from '../Modals/RuleDeleteSelectedModal';
import RuleDisableSelectedModal from '../Modals/RuleDisableSelectedModal';
import RuleDuplicateConfirmModal from '../Modals/RuleDuplicateConfirmModal';
import RuleDuplicateSelectedModal from '../Modals/RuleDuplicateSelectedModal';
import RuleEnableSelectedModal from '../Modals/RuleEnableSelectedModal';
import RuleRecalculateModal from '../../containers/Modals/RuleRecalculateModal';
import RuleSummaryTiles from '../../components/RuleSummaryTiles';
import RulesTable from '../../components/RulesTable';
import BobLoadingMask from '../../components/BobLoadingMask';

import * as BobActions from '../../actions/bobActions';
import * as RuleActions from '../../actions/ruleActions';

const DEFAULT_MODAL_STATES = Object.freeze({
    delete:           false,
    deleteConfirm:    false,
    duplicate:        false,
    duplicateConfirm: false,
    disableSelected:  false,
    enableSelected:   false,
    recalculate:      false
});

const RulesPage = ({
    bobLoading,
    history, 
    fetchRuleKPIs,
    location, 
    kpis,
    setLegacyRuleTablePageSize,
    tablePageSize
}) => {
    const params = new URLSearchParams(location.search);
    const isFullRulesAllowed = usePermissions('rulesFull');
    const [modalStates, setModalStates] = useState({ ...DEFAULT_MODAL_STATES });
    const [redirectTo, setRedirectTo] = useState(null);
    const [rules, fetchRules] = useRules();
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const { selectedRules, selectedRuleIds } = useSelector(
        ({ rules: { items }}) => {
            const selectedRules = [];
            const selectedRuleIds = [];
            // Remove any selected row IDs that are no longer present in the
            // rules state
            for (let rowId of selectedRowIds) {
                if (!items[rowId]) {
                    continue;
                }
                selectedRules.push(items[rowId]);
                selectedRuleIds.push(rowId);
            }
            return { selectedRules, selectedRuleIds };
        }
    );
    const {
        currentPage,
        onCurrentPageChange,
        onExit,
        onPageSizeChange,
        onSearch,
        onSortingChange,
        pageSize,
        pageSizes,
        sorting,
        totalCount
    } = useTableParams(rules, { defaultOrderBy: 'name asc', defaultPageSize: tablePageSize });

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        params.set('pageSize', tablePageSize);
        history.replace({
            search: params.toString()
        });
        fetchRules(Object.fromEntries(params.entries()));
    }, [location.search]);

    useEffect(() => {
        return () => {
            onExit();
        };
    }, []);

    function handleDeleteConfirmation() {
        setModalStates({
            ...DEFAULT_MODAL_STATES,
            delete: true
        });
    }

    function handleDeleteRules() {
        setModalStates({
            ...DEFAULT_MODAL_STATES,
            deleteConfirm: true
        });
    }

    function handleDuplicateRules() {
        setModalStates({
            ...DEFAULT_MODAL_STATES,
            duplicateConfirm: true
        });
    }

    function handleDuplicateConfirmation() {
        setModalStates({
            ...DEFAULT_MODAL_STATES,
            duplicate: true
        });
    }

    async function handleModalClose() {
        setModalStates({ ...DEFAULT_MODAL_STATES });
        await fetchRuleKPIs();
    }

    function handleDisableRules() {
        setModalStates({
            ...DEFAULT_MODAL_STATES,
            disableSelected: true
        });
    }

    function handleEnableRules() {
        setModalStates({
            ...DEFAULT_MODAL_STATES,
            enableSelected: true
        });
    }

    function handleNewRule() {
        setRedirectTo({
            pathname: '/rules/new',
            search:   location.search
        });
    }

    function handleRowSelection(ids) {
        setSelectedRowIds(ids);
    }

    if (redirectTo) {
        return (
            <Redirect push to={redirectTo} />
        );
    }

    const pageHeader = (
        <RuleSummaryTiles
            isLoading={rules.loading}
            kpis={kpis}
        />
    );

    const pageSizeChange = newPageSize => {
        setLegacyRuleTablePageSize(newPageSize);
        onPageSizeChange(newPageSize);
    };

    return (
        <EsmsPage header={pageHeader} title="Rules">
            <ActionBar
                onSearch={onSearch}
                searchValue={params.get('name') || ''}
                isDisabled={bobLoading.includes('RuleSearching')}
            >
                <ActionBarItem
                    disabled={!selectedRuleIds.length}
                    iconSrc="/assets/images/switchEnabledIconColor.svg"
                    onClick={handleEnableRules}
                    hidden={!isFullRulesAllowed}
                >
                    Enable
                </ActionBarItem>
                <ActionBarItem
                    disabled={!selectedRuleIds.length}
                    iconSrc="/assets/images/switchDisabledIconColor.svg"
                    onClick={handleDisableRules}
                    hidden={!isFullRulesAllowed}
                >
                    Disable
                </ActionBarItem>
                <ActionBarItem
                    disabled={!selectedRuleIds.length}
                    iconSrc="/assets/images/duplicateIconColor.svg"
                    onClick={handleDuplicateRules}
                    hidden={!isFullRulesAllowed}
                >
                    Duplicate
                </ActionBarItem>
                <ActionBarItem
                    disabled={!selectedRuleIds.length}
                    onClick={handleDeleteRules}
                    hidden={!isFullRulesAllowed}
                    type="delete"
                >
                    Delete
                </ActionBarItem>
                <ActionBarItem
                    onClick={handleNewRule}
                    hidden={!isFullRulesAllowed}
                    type="add"
                >
                    New Rule
                </ActionBarItem>
            </ActionBar>
            <RulesTable
                currentPage={currentPage}
                error={rules.error}
                isLoading={rules.loading}
                onCurrentPageChange={onCurrentPageChange}
                onPageSizeChange={pageSizeChange}
                onRowSelection={handleRowSelection}
                onSortingChange={onSortingChange}
                pageSize={pageSize}
                pageSizes={pageSizes}
                readOnly={!isFullRulesAllowed}
                rules={rules.data}
                selectedIds={selectedRuleIds}
                sorting={sorting}
                totalCount={totalCount}
            />
            <RuleDisableSelectedModal
                onClose={handleModalClose}
                open={modalStates.disableSelected}
                rules={selectedRules}
            />
            <RuleEnableSelectedModal
                onClose={handleModalClose}
                open={modalStates.enableSelected}
                rules={selectedRules}
            />
            <RuleDeleteConfirmModal
                onConfirm={handleDeleteConfirmation}
                onClose={handleModalClose}
                open={modalStates.deleteConfirm}
                rules={selectedRules}
            />
            <RuleDeleteSelectedModal
                onClose={handleModalClose}
                open={modalStates.delete}
                rules={selectedRules}
            />
            <RuleDuplicateConfirmModal
                onConfirm={handleDuplicateConfirmation}
                onClose={handleModalClose}
                open={modalStates.duplicateConfirm}
                rules={selectedRules}
            />
            <RuleDuplicateSelectedModal
                onClose={handleModalClose}
                open={modalStates.duplicate}
                rules={selectedRules}
            />
            <RuleRecalculateModal
                onClose={handleModalClose}
                open={modalStates.recalculate}
                rules={selectedRules}
            />
            {
                bobLoading.includes('RuleSearching') ?
                    <BobLoadingMask />
                    :
                    null
            }
        </EsmsPage>
    );
};

RulesPage.defaultProps = {
    history:  {},
    location: {}
};

RulesPage.propTypes = {
    history: shape({
        replace: func
    }),
    location: object
};

const mapStateToProps = state => ({
    bobLoading:    state.bob.bobLoading,
    kpis:          state.rules.kpis,
    tablePageSize: state.rules.legacyRuleTablePageSize
});

const mapDispatchToProps = ({
    ...BobActions,
    ...RuleActions
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RulesPage));
